import React from "react";

type PayoutKpiValueProps = {
  value: string | number;
  className?: string;
  compactAt?: number;
};

function parseAmount(value: string | number) {
  if (typeof value === "number") return value;
  const cleaned = value.replace(/[^\d.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatExact(value: string | number, amount: number) {
  if (typeof value === "string") {
    return value.replace(/â‚¹/g, "\u20B9").trim().replace(/\.00$/, "");
  }

  return amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function formatCompact(amount: number) {
  const absolute = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  const trim = (value: number) => value.toFixed(2).replace(/\.00$/, "");

  if (absolute >= 10000000) {
    return `${sign}${trim(absolute / 10000000)} Cr`;
  }

  if (absolute >= 100000) {
    return `${sign}${trim(absolute / 100000)} L`;
  }

  return amount.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function PayoutKpiValue({
  value,
  className = "",
  compactAt = 100000,
}: PayoutKpiValueProps) {
  const amount = parseAmount(value);
  const hasCurrencyPrefix =
    typeof value === "string" && /(\u20B9|â‚¹)/.test(value);
  const shouldCompact = amount !== null && Math.abs(amount) >= compactAt;
  const display =
    amount !== null
      ? shouldCompact
        ? `${hasCurrencyPrefix ? "\u20B9" : ""}${formatCompact(amount)}`
        : formatExact(value, amount)
      : value;
  const exact = amount !== null ? formatExact(value, amount) : String(value);

  if (!shouldCompact) {
    return <>{display}</>;
  }

  return (
    <span className={"group relative inline-flex cursor-default " + className}>
      <span>{display}</span>
      <span className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden whitespace-nowrap rounded-[6px] bg-[#1B163B] px-3 py-2 text-xs font-bold text-white shadow-[0_12px_24px_rgba(27,22,59,0.18)] group-hover:block">
        {exact}
      </span>
    </span>
  );
}

export default PayoutKpiValue;
